import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getAdminId } from '../../utils/getAdminId';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  Plus, Pencil, Trash2, X, Save, Users, Phone, Mail, Briefcase,
  Loader as Loader2,
} from 'lucide-react';

const JOB_FUNCTIONS = [
  'Chief Executive Officer',
  'Customer Service',
  'Store Manager',
  'Warehouse Manager',
  'Developer',
  'Operations Associate',
  'Stock Manager',
  'Client',
  'Principal',
  'Director',
] as const;

interface WorkerContact {
  id: string;
  admin_id: string;
  name: string;
  email: string;
  phone: string | null;
  job_function: string;
  created_at: string;
  updated_at: string;
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  job_function: string;
}

const EMPTY_FORM: FormState = { name: '', email: '', phone: '', job_function: '' };

const BADGE_COLORS: Record<string, string> = {
  'Chief Executive Officer': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  'Customer Service': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'Store Manager': 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  'Warehouse Manager': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  'Developer': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  'Operations Associate': 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  'Stock Manager': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  'Client': 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300',
  'Principal': 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  'Director': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
};

function badgeClass(fn: string) {
  return BADGE_COLORS[fn] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
}

function isValidEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export const WorkersSettingsTab: React.FC = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<WorkerContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const adminId = getAdminId(user);

  const load = useCallback(async () => {
    if (!adminId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('worker_contacts')
      .select('*')
      .eq('admin_id', adminId)
      .order('name');
    if (!error && data) setContacts(data);
    setLoading(false);
  }, [adminId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (c: WorkerContact) => {
    setEditingId(c.id);
    setForm({ name: c.name, email: c.email, phone: c.phone ?? '', job_function: c.job_function });
    setShowForm(true);
  };

  const cancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!adminId) return;
    if (!form.name.trim() || !form.email.trim() || !form.job_function) {
      alert('Name, email, and job function are required.');
      return;
    }
    if (!isValidEmail(form.email.trim())) {
      alert('Please enter a valid email address.');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from('worker_contacts')
          .update({
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim() || null,
            job_function: form.job_function,
          })
          .eq('id', editingId)
          .eq('admin_id', adminId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('worker_contacts')
          .insert({
            admin_id: adminId,
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim() || null,
            job_function: form.job_function,
          });
        if (error) {
          if (error.code === '23505') {
            alert('A worker with this email already exists.');
            return;
          }
          throw error;
        }
      }
      cancel();
      await load();
    } catch (err: any) {
      console.error('Save worker contact failed:', err);
      alert('Failed to save: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Remove this worker contact?')) return;
    setDeleting(id);
    try {
      const { error } = await supabase.from('worker_contacts').delete().eq('id', id);
      if (error) throw error;
      setContacts((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      alert('Delete failed: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Workers Function &amp; Emails
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage your team contacts, job functions, and email addresses.
            {contacts.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                {contacts.length} worker{contacts.length !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        {!showForm && (
          <Button onClick={openCreate} variant="primary" size="sm">
            <Plus className="h-4 w-4 mr-1.5" />
            Add Worker
          </Button>
        )}
      </div>

      {showForm && (
        <div className="mb-6 border border-gray-200 dark:border-gray-700 rounded-xl p-5 bg-gray-50 dark:bg-gray-800/50">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {editingId ? 'Edit Worker' : 'New Worker'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Full name"
              required
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="worker@company.com"
              required
            />
            <Input
              label="Phone (optional)"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+31 6 12345678"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Job Function
              </label>
              <select
                value={form.job_function}
                onChange={(e) => setForm({ ...form, job_function: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                required
              >
                <option value="">Select a function...</option>
                {JOB_FUNCTIONS.map((fn) => (
                  <option key={fn} value={fn}>{fn}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <Button onClick={cancel} variant="secondary" size="sm">
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button onClick={handleSave} variant="primary" size="sm" loading={saving}>
              <Save className="h-4 w-4 mr-1.5" />
              {editingId ? 'Update' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
            No workers added yet
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
            Add your team members with their job functions and contact details.
          </p>
          {!showForm && (
            <Button onClick={openCreate} variant="primary" size="sm">
              <Plus className="h-4 w-4 mr-1.5" />
              Add First Worker
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map((c) => (
            <div
              key={c.id}
              className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {c.name}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass(c.job_function)}`}>
                    <Briefcase className="h-3 w-3 mr-1" />
                    {c.job_function}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                  <span className="inline-flex items-center gap-1 truncate">
                    <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                    {c.email}
                  </span>
                  {c.phone ? (
                    <span className="inline-flex items-center gap-1">
                      <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                      {c.phone}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-gray-400 dark:text-gray-500">
                      <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                      --
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => openEdit(c)}
                  className="p-2 text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(c.id)}
                  disabled={deleting === c.id}
                  className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                  title="Delete"
                >
                  {deleting === c.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
